"use client";

import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { AdsBannerComponentWithItems } from "@/util/query/ads-banner-component";
import { getBaseUrl } from "@/util/get-base-url";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import "./ImageSlideshow.css";

import { ChevronLeft, ChevronRight } from "lucide-react";

type Props = {
    adsBannerComponent: AdsBannerComponentWithItems;
    priorityImage?: boolean;
    loc: string;
    mobile: boolean;
    preview?: boolean;
    colorStyle?: any;
};

export const AdsSectionCarousel = ({ adsBannerComponent, priorityImage, loc, mobile, preview, colorStyle }: Props) => {
    const autoSlide = adsBannerComponent.autoSlide || undefined;
    const slideDelay = adsBannerComponent.slideDelay || 4000;
    const slideSpeed = 500; // Define a constant for slide speed

    const sectionRef = useRef(null);
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const [swiperInstance, setSwiperInstance] = useState<any>(null);

    useEffect(() => {
        if (prevRef.current && nextRef.current && swiperInstance) {
            // Update navigation elements
            swiperInstance.params.navigation.prevEl = prevRef.current;
            swiperInstance.params.navigation.nextEl = nextRef.current;
            swiperInstance.navigation.init();
            swiperInstance.navigation.update();
        }
    }, [swiperInstance]);

    const autoPlayOptions = autoSlide && !preview
    ? {
        delay: slideDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
    }
    : undefined

    const swiperModules = [
        Navigation,
        Pagination,
        ...(autoSlide && !preview ? [Autoplay] : []),
    ];

    return (
        <div className="relative w-full h-full web-banner-component" 
            dir={"ltr"}>
            <Swiper
                modules={swiperModules}
                effect="slide"
                allowTouchMove={true} // Enable drag functionality
                slidesPerView="auto"
                loop
                autoplay={autoPlayOptions}
                speed={slideSpeed}
                navigation={{
                    prevEl: prevRef.current,
                    nextEl: nextRef.current,
                }}
                pagination={{ clickable: true }}
                style= {{
                    "--swiper-pagination-bullet-color": colorStyle?.buttonBgNormal || "#009eca"
                } as React.CSSProperties}
                className="w-full h-full"
                onSwiper={setSwiperInstance}
            >
                {adsBannerComponent.items.map((item, index) => {
                    return (
                        item.attributes?.desktopImageUrl && (
                            <SwiperSlide key={item.id}>
                                <a className="hidden sm:block" href={item.attributes.linkUrl} target={item.linkTarget == "SELF" ? "_self" : "_blank"} aria-label="banner">
                                    <div
                                        style={{
                                            paddingTop: `${
                                                adsBannerComponent?.paddingTop?.[loc.toLowerCase()]?.desktop ??
                                                "undefined"
                                            }%`,
                                        }}>
                                        <picture>
                                            <source
                                                srcSet={`${getBaseUrl(item.attributes.desktopImageUrl || "")}.avif`}
                                                type="image/avif"
                                            />
                                            <source
                                                srcSet={`${getBaseUrl(item.attributes.desktopImageUrl || "")}.webp`}
                                                type="image/webp"
                                            />
                                            <img
                                                src={item.attributes.desktopImageUrl || ""}
                                                width="1180"
                                                height="489"
                                                className={`w-full hidden sm:block ${
                                                    adsBannerComponent.paddingTop ? "absolute top-0" : ""
                                                }`}
                                                fetchPriority={priorityImage ? (index === 0 ? "high" : "auto") : "auto"}
                                                loading={priorityImage ? (index === 0 ? "eager" : "lazy") : "lazy"}
                                                alt={item.attributes.imageAlt || `ads-banner-${index}`}
                                            />
                                        </picture>
                                    </div>
                                </a>

                                <a className="block sm:hidden" href={item.attributes.linkUrl} target="_self" aria-label="banner">
                                    <div
                                        style={{
                                            paddingTop: `${
                                                adsBannerComponent.paddingTop?.[loc.toLowerCase()]?.mobile ?? "undefined"
                                            }%`,
                                        }}>
                                        <picture>
                                            <source
                                                srcSet={`${getBaseUrl(item.attributes.mobileImageUrl || "")}.avif`}
                                                type="image/avif"
                                            />
                                            <source
                                                srcSet={`${getBaseUrl(item.attributes.mobileImageUrl || "")}.webp`}
                                                type="image/webp"
                                            />
                                            <img
                                                src={item.attributes.mobileImageUrl || ""}
                                                width="370"
                                                height="153"
                                                className={`w-full block sm:hidden ${
                                                    adsBannerComponent.paddingTop ? "absolute top-0" : ""
                                                }`}
                                                fetchPriority={priorityImage ? (index === 0 ? "high" : "auto") : "auto"}
                                                loading={priorityImage ? (index === 0 ? "eager" : "lazy") : "lazy"}
                                                alt={item.attributes.imageAlt || `ads-banner-${index}`}
                                            />
                                        </picture>
                                    </div>
                                </a>
                            </SwiperSlide>
                        ))
                    }
                )}
            </Swiper>

            {/* Custom Navigation Buttons */}

            <button
                aria-label="Previous Slide"
                ref={prevRef}
                className="hidden sm:block custom-prev absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 z-10 p-2 lg:p-3 opacity-60 rounded-full text-white dynamic-bg dynamic-color"
                style={
                    {
                        "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                        "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                        "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                        "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                        "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                    } as React.CSSProperties
                }
            >
                <ChevronLeft className="h-3 w-3 lg:h-5 lg:w-5" />
            </button>
            <button
                aria-label="Next Slide"
                ref={nextRef}
                className="hidden sm:block custom-next absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 z-10 p-2 lg:p-3 opacity-60 rounded-full text-white dynamic-bg dynamic-color"
                style={{
                        "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                        "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                        "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                        "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                        "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                    } as React.CSSProperties
                }
            >
                <ChevronRight className="h-3 w-3 lg:h-5 lg:w-5" />
            </button>
        </div>
    );
};

export default AdsSectionCarousel;
